import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { animate, motion, useAnimationControls, useMotionValue, useReducedMotion } from "motion/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import * as Flags from "country-flag-icons/react/3x2";
import { EASE_EXPO } from "@/components/motion/primitives";
import { stories as REAL, type Story } from "@/data/stories";

const INK = "#0A0F0E";
const BRAND_DARK = "#00332F";
const TEAL = "#2EE6D6";
const PANEL = "#F2F2ED";
const MUTED = "#69716E";

/*
 * People who have used Migrada: distinct emails across accounts, plan
 * requests, questions, checklist and readiness requests on production,
 * without the founder's and test addresses. 42 on 3 October 2026. Round down
 * to the nearest ten and update it as it grows; never round up.
 */
const REACHED = "40+";
/** Members of Migrada's student community on Reddit (Noah's figure, 3 October 2026). Round down. */
const COMMUNITY_SIZE = "500+";

/*
 * Wise's "For people going places", ours: real people who moved to Austria,
 * in their own words. Only stories.ts is shown in production, and while it
 * is empty the section does not render at all. The development build shows
 * clearly marked samples so the design can be judged.
 *
 * Cards alternate teal and dark green: a large round flag, the quote and a
 * name pill with the route. Arrows or a swipe move the row; cards moved past
 * fade backwards, returning cards slide up, and the arriving card's flag
 * rolls into place.
 */
const STORIES: Story[] =
  REAL.length > 0
    ? REAL
    : process.env.NODE_ENV === "development"
      ? (require("@/data/stories-sample") as { SAMPLE_STORIES: Story[] }).SAMPLE_STORIES
      : [];

function Flag({ code, size }: { code: string; size: number }) {
  const F = Flags[code as keyof typeof Flags] as React.ComponentType<{ className?: string }> | undefined;
  return (
    <span className="relative block overflow-hidden rounded-full" style={{ width: size, height: size }}>
      {F && <F className="absolute left-1/2 top-0 h-full w-auto max-w-none -translate-x-1/2" />}
    </span>
  );
}

/**
 * One story. Cards the row has moved past fade backwards (smaller, fainter);
 * when the row comes back, a returning card slides up and fades in, as on
 * Wise.
 */
function Card({ story, i, index, width }: { story: Story; i: number; index: number; width: number }) {
  const dark = i % 2 === 0; // starts dark: Kazakhstan's sky blue clashes with teal
  const fg = dark ? TEAL : INK;
  const reduced = useReducedMotion() ?? false;
  const passed = i < index;
  const active = i === index;
  const controls = useAnimationControls();
  const wasPassed = useRef(passed);

  useEffect(() => {
    if (reduced) return;
    if (passed) controls.start({ opacity: 0, scale: 0.86, y: 0, transition: { duration: 0.45, ease: EASE_EXPO } });
    else if (wasPassed.current) controls.start({ opacity: [0, 1], scale: [0.96, 1], y: [48, 0], transition: { duration: 0.6, ease: EASE_EXPO } });
    wasPassed.current = passed;
  }, [passed, controls, reduced]);

  return (
    <motion.li
      className="relative flex shrink-0 origin-left flex-col p-6 sm:p-8"
      style={{ width, height: width * 1.38, borderRadius: 32, backgroundColor: dark ? BRAND_DARK : TEAL }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: i * 0.08, ease: EASE_EXPO }}
      animate={controls}
      aria-hidden={passed}
    >
      <motion.span
        className="self-start"
        animate={reduced ? undefined : { rotate: active ? 0 : -24, scale: active ? 1 : 0.92 }}
        transition={{ type: "spring", stiffness: 180, damping: 16 }}
        style={{ boxShadow: dark ? "0 0 0 4px rgba(46,230,214,0.18)" : "0 0 0 4px rgba(0,51,47,0.12)", borderRadius: 999 }}
      >
        <Flag code={story.countryCode} size={width > 320 ? 128 : 96} />
      </motion.span>
      <blockquote className="mt-7 flex-1 text-[20px] font-semibold leading-[1.35] tracking-[-0.01em] sm:text-[23px]" style={{ color: fg }}>
        “{story.quote}”
      </blockquote>
      <p className="mt-6 self-start rounded-full px-5 py-2.5 text-[15px] font-bold" style={{ backgroundColor: dark ? TEAL : BRAND_DARK, color: dark ? BRAND_DARK : TEAL }}>
        {story.name} · {story.route}
      </p>
    </motion.li>
  );
}

export function Stories() {
  const viewport = useRef<HTMLDivElement>(null);
  const [vw, setVw] = useState(800);
  useLayoutEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const measure = () => setVw(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const phone = vw < 520;
  const width = phone ? Math.min(300, vw - 40) : 400;
  const gap = phone ? 14 : 22;
  const step = width + gap;
  const last = Math.max(0, STORIES.length - 1);

  const [index, setIndex] = useState(0);
  const x = useMotionValue(0);
  const go = (n: number) => {
    const to = Math.max(0, Math.min(last, n));
    setIndex(to);
    animate(x, -to * step, { type: "spring", stiffness: 220, damping: 30 });
  };

  if (STORIES.length === 0) return null;

  return (
    <section className="overflow-hidden bg-white py-16 sm:py-24" aria-roledescription="carousel" aria-label="Stories from people who moved to Austria">
      <div className="mx-auto grid max-w-[1180px] grid-cols-1 items-center gap-10 px-5 sm:px-8 lg:grid-cols-[minmax(0,540px)_minmax(0,1fr)] lg:gap-14">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: EASE_EXPO }}>
          <p className="flex items-center gap-2 text-[15px] font-semibold" style={{ color: MUTED }}>
            <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: TEAL, color: BRAND_DARK }} aria-hidden="true">
              ★
            </span>
            {REACHED} students on Migrada and growing · {COMMUNITY_SIZE} in our community
          </p>
          <h2 className="mt-5 font-black uppercase leading-[0.9] tracking-[-0.045em]" style={{ color: INK, fontSize: "clamp(2.4rem, 1.4rem + 3.4vw, 4rem)" }}>
            People who made the move
          </h2>
          <div className="mt-8 flex gap-3">
            <button
              type="button"
              onClick={() => go(index - 1)}
              disabled={index === 0}
              className="flex h-14 w-14 items-center justify-center rounded-full transition-transform enabled:hover:-translate-x-0.5 disabled:opacity-40 sm:h-16 sm:w-16"
              style={{ backgroundColor: PANEL, color: INK }}
              aria-label="Previous story"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              disabled={index === last}
              className="flex h-14 w-14 items-center justify-center rounded-full transition-transform enabled:hover:translate-x-0.5 disabled:opacity-40 sm:h-16 sm:w-16"
              style={{ backgroundColor: TEAL, color: BRAND_DARK }}
              aria-label="Next story"
            >
              <ArrowRight className="h-6 w-6" />
            </button>
          </div>
        </motion.div>

        {/* The row runs off the right edge, like Wise's. Drag or swipe it too. */}
        <div ref={viewport} className="relative -mr-5 overflow-hidden sm:-mr-8 lg:-mr-[calc((100vw_-_1180px)/2_+_32px)]">
          <motion.ul
            className="flex cursor-grab active:cursor-grabbing"
            style={{ x, gap }}
            drag="x"
            dragConstraints={{ left: -last * step, right: 0 }}
            dragElastic={0.12}
            onDragEnd={(_, info) => go(Math.round((-x.get() - info.velocity.x * 0.2) / step))}
          >
            {STORIES.map((s, i) => (
              <Card key={s.id} story={s} i={i} index={index} width={width} />
            ))}
          </motion.ul>
        </div>
      </div>
    </section>
  );
}
